import React from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import { renderStore } from "../../../../store/renderStore";
import {
  ClippingPolygonType,
  SymbolType,
  WallType,
  isDoubleDoorType,
  isSingleLeafDoorType,
  isVoidType,
  isWindowType,
} from "../../../../types/floorplannerTypes";
import { resolveMaterial } from "../../materials/materialResolver";
import ExtrudedShapeMesh from "../../scene/ExtrudedShapeMesh";
import HiddenOccluderEdges from "../../scene/HiddenOccluderEdges";
import SelectedMeshOutline from "../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../scene/usePointerUpSelection";
import {
  applyWallTextureUVs,
  applyWallEndpointHeights,
  buildWallExtrudeShapes,
  createStraightWallOpeningGeometry,
  getWall3DHeight,
  getWallColor,
  getWallOpeningGeometryClippings,
  shouldUseIntegratedOpeningGeometry,
  WallEndpointHeights,
} from "./wallGeometry";
import Wall3DMaterial from "./Wall3DMaterial";

const Wall3DMeshes: React.FC<{
  wall: WallType;
  heightProfile?: WallEndpointHeights;
  selected: boolean;
  cameraHidden: boolean;
  claimSelectionEvents?: boolean;
  onSelect: (wall: WallType, event: ThreeEvent<PointerEvent>) => void;
}> = observer(
  ({
    wall,
    heightProfile,
    selected,
    cameraHidden,
    claimSelectionEvents = false,
    onSelect,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { gl } = useThree();
    const selectionHandlers = usePointerUpSelection(wall, onSelect, {
      claimPointerEvents: claimSelectionEvents,
    });

    const wallShape = renderStore.innerWallShapesMap.get(wall.id)?.[0];
    const wallClippings = renderStore.wallClippingsMap.get(wall.id);
    const combinedWallClippings = getWallOpeningGeometryClippings(
      wall,
      floorplannerStore.wallsMap.values(),
      floorplannerStore.wallWidth,
      wallClippings,
    );
    const wallClippingSignature = getWallClippingSignature(
      combinedWallClippings,
    );
    const openingGeometrySignature = getOpeningGeometrySignature(
      wall,
      floorplannerStore.symbolsMap,
    );
    const openingSymbols = React.useMemo(
      () =>
        (wall.symbolAttachments ?? [])
          .map((attachment) =>
            floorplannerStore.symbolsMap.get(attachment.symbolId),
          )
          .filter(isOpeningSymbol),
      [floorplannerStore.symbolsMap, openingGeometrySignature, wall],
    );
    const height = getWall3DHeight(wall);
    const resolvedHeightProfile = heightProfile ?? {
      start: height,
      end: height,
    };
    const extrusionHeight = Math.max(
      height,
      resolvedHeightProfile.start,
      resolvedHeightProfile.end,
    );
    const useIntegratedGeometry = shouldUseIntegratedOpeningGeometry(
      wall,
      wallShape,
      combinedWallClippings,
      openingSymbols,
    );
    const straightGeometry = React.useMemo(
      () => {
        if (cameraHidden || !useIntegratedGeometry) return null;
        const geometry = createStraightWallOpeningGeometry(
          wall,
          openingSymbols,
          {
            wallShape,
            wallWidth: wall.wallWidth ?? floorplannerStore.wallWidth,
            wallHeight: extrusionHeight,
            defaultDoorFrameWidth: floorplannerStore.doorFrameWidth,
            defaultWindowFrameWidth: floorplannerStore.windowFrameWidth,
            clippingPolygons: combinedWallClippings,
          },
        );
        if (geometry) {
          applyWallEndpointHeights(
            geometry,
            wall,
            resolvedHeightProfile,
            extrusionHeight,
          );
          applyWallTextureUVs(geometry, wall);
        }
        return geometry;
      },
      [
        floorplannerStore.doorFrameWidth,
        floorplannerStore.wallWidth,
        floorplannerStore.windowFrameWidth,
        cameraHidden,
        extrusionHeight,
        // Attachment slides (arrow keys) change this signature → rebuild holes.
        openingGeometrySignature,
        openingSymbols,
        resolvedHeightProfile.end,
        resolvedHeightProfile.start,
        wall,
        wallShape,
        useIntegratedGeometry,
        wallClippingSignature,
      ],
    );
    const fallbackShapes = React.useMemo(
      () =>
        cameraHidden || useIntegratedGeometry
          ? []
          : buildWallExtrudeShapes(
              wallShape,
              combinedWallClippings,
              wall.lineWeight ?? floorplannerStore.wallLineWeight,
              wall.wallWidth ?? floorplannerStore.wallWidth,
            ),
      [
        cameraHidden,
        floorplannerStore.wallLineWeight,
        floorplannerStore.wallWidth,
        useIntegratedGeometry,
        wall.lineWeight,
        wall.wallWidth,
        wallShape,
        wallClippingSignature,
      ],
    );
    const hiddenEdgeShapes = React.useMemo(
      () =>
        cameraHidden
          ? buildWallExtrudeShapes(
              wallShape,
              undefined,
              wall.lineWeight ?? floorplannerStore.wallLineWeight,
              wall.wallWidth ?? floorplannerStore.wallWidth,
            )
          : [],
      [
        cameraHidden,
        floorplannerStore.wallLineWeight,
        floorplannerStore.wallWidth,
        wall.lineWeight,
        wall.wallWidth,
        wallShape,
      ],
    );
    const configureWallTextureUVs = React.useCallback(
      (geometry: THREE.ExtrudeGeometry) => {
        applyWallEndpointHeights(
          geometry,
          wall,
          resolvedHeightProfile,
          extrusionHeight,
        );
        applyWallTextureUVs(geometry, wall);
      },
      [
        extrusionHeight,
        resolvedHeightProfile.end,
        resolvedHeightProfile.start,
        wall,
      ],
    );
    const configureHiddenWallEdges = React.useCallback(
      (geometry: THREE.BufferGeometry) => {
        applyWallEndpointHeights(
          geometry,
          wall,
          resolvedHeightProfile,
          extrusionHeight,
        );
      },
      [
        extrusionHeight,
        resolvedHeightProfile.end,
        resolvedHeightProfile.start,
        wall,
      ],
    );
    React.useEffect(
      () => () => {
        straightGeometry?.dispose();
      },
      [straightGeometry],
    );

    const color = resolveMaterial("wall", wall, {
      defaultColor: getWallColor(wall),
    }).color;

    if (cameraHidden) {
      return (
        <>
          {hiddenEdgeShapes.map((shape, index) => (
            <HiddenOccluderEdges
              key={`${wall.id}-edge-${index}`}
              shape={shape}
              mode="vertical"
              height={extrusionHeight}
              color={color}
              configureGeometry={configureHiddenWallEdges}
              userData={{
                floorplannerType: "wall-edge",
                floorplannerId: wall.id,
              }}
            />
          ))}
        </>
      );
    }

    if (straightGeometry) {
      return (
        <mesh
          geometry={straightGeometry}
          castShadow
          receiveShadow
          userData={{ floorplannerType: "wall", floorplannerId: wall.id }}
          {...selectionHandlers}
          onPointerOver={() => {
            gl.domElement.style.cursor = "pointer";
          }}
          onPointerOut={() => {
            gl.domElement.style.cursor = "default";
          }}
        >
          <Wall3DMaterial wall={wall} color={color} />
          <SelectedMeshOutline visible={selected} />
        </mesh>
      );
    }

    return (
      <>
        {fallbackShapes.map((shape, index) => (
          <ExtrudedShapeMesh
            key={`${wall.id}-${index}`}
            shape={shape}
            depth={extrusionHeight}
            selected={selected}
            receiveShadow
            configureGeometry={configureWallTextureUVs}
            {...selectionHandlers}
            userData={{ floorplannerType: "wall", floorplannerId: wall.id }}
          >
            <Wall3DMaterial wall={wall} color={color} />
          </ExtrudedShapeMesh>
        ))}
      </>
    );
  },
);

function isOpeningSymbol(symbol: SymbolType | undefined): symbol is SymbolType {
  return (
    !!symbol &&
    (isSingleLeafDoorType(symbol) ||
      isDoubleDoorType(symbol) ||
      isWindowType(symbol) ||
      isVoidType(symbol))
  );
}

function getOpeningGeometrySignature(
  wall: WallType,
  symbolsMap: Map<string, SymbolType>,
): string {
  return (wall.symbolAttachments ?? [])
    .map((attachment) => {
      const symbol = symbolsMap.get(attachment.symbolId);
      return [
        attachment.symbolId,
        attachment.positionFromStart,
        symbol?.type ?? "",
        symbol && isSingleLeafDoorType(symbol) ? symbol.doorWidth : "",
        symbol && isDoubleDoorType(symbol) ? symbol.doubleDoorWidth : "",
        symbol && isWindowType(symbol) ? symbol.windowLength : "",
        symbol && isVoidType(symbol) ? symbol.voidLength : "",
        symbol && (isSingleLeafDoorType(symbol) || isDoubleDoorType(symbol))
          ? (symbol.doorFrameWidth ?? "")
          : "",
        symbol && isWindowType(symbol) ? (symbol.windowFrameWidth ?? "") : "",
        symbol?.threeD?.height ?? "",
        symbol?.threeD?.baseElevation ?? "",
        symbol?.threeD?.topBulge ?? "",
      ].join(":");
    })
    .join("|");
}

function getWallClippingSignature(
  clippings: ClippingPolygonType[] | undefined,
): string {
  return (clippings ?? [])
    .map((clipping) =>
      [
        clipping.belongsTo,
        clipping.objectType,
        clipping.polygon.map((point) => `${point.x},${point.y}`).join(";"),
      ].join(":"),
    )
    .join("|");
}

export default Wall3DMeshes;
